import { Env } from '@protector/utils';
import mapboxgl from 'mapbox-gl';
import { useEffect, useRef } from 'react';
import type { CoreMapProps } from './core-map.models';
import styles from './core-map.module.css';
import { useMapLanguage } from './hooks/useMapLanguage.hook';

export const CoreMap = ({
  style,
  center,
  zoom = 12,
  minZoom,
  maxZoom,
  onMapLoaded,
  navigationControl,
  preserveDrawingBuffer = false,
  isMultipleMap = false
}: CoreMapProps): JSX.Element => {
  const mapHTMLContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map>();

  const { languageControl } = useMapLanguage({ map: map.current, style });

  useEffect(() => {
    if (!mapHTMLContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      accessToken: Env.MAPBOX_KEY,
      container: mapHTMLContainer.current,
      style,
      center,
      attributionControl: false,
      crossSourceCollisions: true,
      zoom,
      minZoom,
      maxZoom,
      preserveDrawingBuffer
    });

    map.current.addControl(languageControl);

    map.current.on('load', () => {
      if (!map.current) return;

      map.current.resize();
      onMapLoaded?.(map.current);

      if (navigationControl) {
        map.current.addControl(
          new mapboxgl.NavigationControl({
            showCompass: false,
            visualizePitch: false
          }),
          'bottom-right'
        );
      }
    });
  }, [center, languageControl, maxZoom, minZoom, navigationControl, onMapLoaded, preserveDrawingBuffer, style, zoom]);

  useEffect(() => {
    return () => {
      if (isMultipleMap) return;
      map.current?.remove?.();
    };
  }, [isMultipleMap]);

  return (
    <div className={styles.mapWrapper} data-testid='sd-map'>
      <div className={styles.mapContainer} ref={mapHTMLContainer} />
    </div>
  );
};
